import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { Appointment } from '../../types/appointment';
import { ClinicalCard, AlertBadge, TimelineItem } from './shared';
import { formatDate } from '../../utils/dateFormat';
import { usePatientAppointments } from '../../hooks/usePatientCockpit';
import LoadingSpinner from '../LoadingSpinner';

/**
 * AppointmentHistory - Displays recent appointments with transcription status
 * Provides quick access to appointment details and transcripts
 */
interface AppointmentHistoryProps {
  patientId: string;
  limit?: number;
  onViewTranscript?: (appointmentId: string) => void;
  onScheduleFollowup?: (appointment: Appointment) => void;
  className?: string;
}

const AppointmentHistory: React.FC<AppointmentHistoryProps> = ({
  patientId,
  limit = 5,
  onViewTranscript,
  onScheduleFollowup,
  className = ''
}) => {
  const navigate = useNavigate();
  const [selectedAppointment, setSelectedAppointment] = useState<string | null>(null);
  
  const { 
    appointments, 
    loading, 
    error, 
    appointmentStats,
    refetch 
  } = usePatientAppointments(patientId, { 
    limit, 
    sortBy: 'scheduledDate', 
    order: 'desc' 
  });

  const getAppointmentStatusConfig = (appointment: Appointment) => {
    const recordingStatus = appointment.recording.status;
    const transcriptionStatus = appointment.transcription.status;
    
    if (transcriptionStatus === 'completed') {
      return {
        status: 'completed',
        icon: <CheckCircleIcon className="w-4 h-4" />,
        color: 'green',
        label: 'Transcribed'
      };
    }
    
    if (transcriptionStatus === 'processing') {
      return {
        status: 'processing',
        icon: <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />,
        color: 'blue',
        label: 'Processing'
      };
    }
    
    if (transcriptionStatus === 'failed') {
      return {
        status: 'failed',
        icon: <XMarkIcon className="w-4 h-4" />,
        color: 'red',
        label: 'Failed'
      };
    }
    
    if (recordingStatus === 'completed') {
      return {
        status: 'recorded',
        icon: <DocumentTextIcon className="w-4 h-4" />,
        color: 'yellow',
        label: 'Recorded'
      };
    }
    
    return {
      status: 'pending',
      icon: <CalendarIcon className="w-4 h-4" />,
      color: 'gray',
      label: 'Scheduled'
    };
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment.id);
    navigate(`/appointments/${appointment.id}`);
  };

  const handleViewTranscript = (appointmentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onViewTranscript) {
      onViewTranscript(appointmentId);
    }
  };

  if (loading) {
    return (
      <ClinicalCard title="Recent Appointments" icon={<CalendarIcon className="w-5 h-5" />}>
        <div className="flex justify-center py-8">
          <LoadingSpinner inline size="sm" />
          <span className="ml-3 text-gray-600">Loading appointments...</span>
        </div>
      </ClinicalCard>
    );
  }

  if (error) {
    return (
      <ClinicalCard title="Recent Appointments" icon={<CalendarIcon className="w-5 h-5" />}>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading appointments
              </h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={refetch}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </ClinicalCard>
    );
  }

  return (
    <ClinicalCard
      title="Recent Appointments"
      icon={<CalendarIcon className="w-5 h-5" />}
      className={className}
      headerAction={
        <button
          onClick={() => navigate('/appointments')}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          View All →
        </button>
      }
    >
      {/* Appointment Statistics */}
      <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{appointmentStats.total}</div>
          <div className="text-xs text-gray-600">Total</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{appointmentStats.withTranscripts}</div>
          <div className="text-xs text-gray-600">Transcribed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{appointmentStats.transcriptionRate}%</div>
          <div className="text-xs text-gray-600">Success Rate</div>
        </div>
      </div>

      {/* Appointments List */}
      {appointments.length === 0 ? (
        <div className="text-center py-8">
          <CalendarIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">No appointments found</p>
          <button
            onClick={() => navigate(`/appointments/new?patientId=${patientId}`)}
            className="btn-primary"
          >
            Schedule First Appointment
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {appointments.map((appointment, index) => {
            const statusConfig = getAppointmentStatusConfig(appointment);
            const isLast = index === appointments.length - 1;
            
            return (
              <TimelineItem
                key={appointment.id}
                date={appointment.appointmentDetails.actualDate || appointment.appointmentDetails.scheduledDate}
                title={`${appointment.appointmentDetails.type} Appointment`}
                description={appointment.appointmentDetails.chiefComplaint}
                type="appointment"
                status={statusConfig.status as any}
                isLast={isLast}
                onClick={() => handleAppointmentClick(appointment)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <AlertBadge type={statusConfig.color as any} size="sm">
                      <div className="flex items-center">
                        {statusConfig.icon}
                        <span className="ml-1">{statusConfig.label}</span>
                      </div>
                    </AlertBadge>
                    
                    {appointment.appointmentDetails.duration && (
                      <div className="flex items-center text-xs text-gray-500">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        {Math.round(appointment.appointmentDetails.duration / 60)}min
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    {appointment.transcription.status === 'completed' && (
                      <button
                        onClick={(e) => handleViewTranscript(appointment.id, e)}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center"
                      >
                        <DocumentTextIcon className="w-3 h-3 mr-1" />
                        View Transcript
                      </button>
                    )}
                    
                    {appointment.recording.status === 'pending' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/recording/${appointment.id}`);
                        }}
                        className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center"
                      >
                        <PlayIcon className="w-3 h-3 mr-1" />
                        Start Recording
                      </button>
                    )}
                  </div>
                </div>

                {/* Transcription metadata */}
                {appointment.transcription.status === 'completed' && (
                  <div className="mt-2 text-xs text-gray-600 bg-green-50 px-2 py-1 rounded">
                    {appointment.transcription.confidence && (
                      <span>Confidence: {Math.round(appointment.transcription.confidence * 100)}%</span>
                    )}
                    {appointment.transcription.wordCount && (
                      <span className="ml-2">Words: {appointment.transcription.wordCount}</span>
                    )}
                  </div>
                )}

                {/* Error information */}
                {appointment.transcription.status === 'failed' && appointment.transcription.errorMessage && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                    Error: {appointment.transcription.errorMessage}
                  </div>
                )}
              </TimelineItem>
            );
          })}
        </div>
      )}

      {/* Footer Actions */}
      {appointments.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={() => navigate(`/appointments/new?patientId=${patientId}`)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            + Schedule New Appointment
          </button>
          
          {onScheduleFollowup && appointments[0] && (
            <button
              onClick={() => onScheduleFollowup(appointments[0])}
              className="text-sm text-gray-600 hover:text-gray-700 font-medium"
            >
              Schedule Follow-up
            </button>
          )}
        </div>
      )}
    </ClinicalCard>
  );
};

export default AppointmentHistory;